// Models browser — lives under /ai/models. Shows every model we have
// pricing for (aggregated across all ACTIVE+isPublic PER_TOKEN endpoints) and
// drills into per-provider pricing on /ai/models/:slug. Intentionally lighter
// than OpenRouter's Models pages — we only surface what we actually track
// (price, providers, streaming, estimated max output). Anything we don't
// have (modality, weekly tokens, latency/uptime) is omitted rather than faked.

import React from 'react';
import { XPrim, XIcons } from '../primitives.jsx';
import { XApi } from '../services.js';
import { audioPriceText, ModelReferencePrices } from '../components/ModelReferencePrices.jsx';
import { formatPrice, modelCategory } from './model-pricing.js';
import { XShell } from '../shell.jsx';
import { Footer, ApiAvatar } from './market.jsx';

// ApiAvatar's letter-fallback only gets its category-tinted background when
// caller passes a `meta` object (categoryMeta lookup). Without it the fallback
// is flat grey — which is what was leaving the detail page avatars looking
// dead. Centralizing the lookup here so list/detail can't drift.
const avatarMetaFor = (provider) =>
  XPrim.categoryMeta(provider?.apiServiceCategory || '');

// --------- shared helpers ---------

// 模型 id 展示统一规范化：lowercase + `.` → `-`（跟后端 catalog 命名规则一致）。
// 即便后端返回的是 unmapped 上游 raw 名（带点/大写），前端也保证显示规范形。
const normalizeId = (s) => String(s || '').trim().toLowerCase().replaceAll('.', '-');

// vendor 字段后端是全小写（"anthropic"），UI 显示成 "Anthropic" 等。
const vendorDisplay = (v) => {
  if (!v) return '';
  const map = {
    openai: 'OpenAI', anthropic: 'Anthropic', google: 'Google', deepseek: 'DeepSeek',
    xai: 'xAI', alibaba: '阿里 Qwen', moonshot: 'Moonshot', zhipu: '智谱 GLM',
    mistral: 'Mistral', minimax: 'MiniMax', bytedance: '字节 Doubao', xiaomi: '小米 MiMo',
    kuaishou: '快手 Kling', tencent: '腾讯 Hunyuan', lumalabs: 'Luma', runwayml: 'Runway', pikalabs: 'Pika',
  };
  return map[v] || v.charAt(0).toUpperCase() + v.slice(1);
};

// tokenPricing is stored per-token; the UI consistently shows per-1M tokens
// (matches every model card we benchmarked against — OpenRouter, Together, etc).
const per1M = (perToken) => perToken == null ? null : Number(perToken) * 1_000_000;
const fmtUsd = formatPrice;
const fmtCtx = (n) => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

// Keep explicit free prices distinct from missing prices.
const safeMin = (arr) => {
  const nums = arr.filter((n) => n != null && Number.isFinite(n) && n >= 0);
  return nums.length ? Math.min(...nums) : null;
};

// ============ List page ============

export const ModelsListPage = () => {
  const { navigate } = XShell.useRoute();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [q, setQ] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    XApi.modelsService
      .list()
      .then((list) => {
        if (!cancelled) setItems(Array.isArray(list) ? list : []);
      })
      .catch((e) => !cancelled && setErr(e.message || 'Failed to load models'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = React.useMemo(() => {
    if (!q.trim()) return items;
    const lo = q.toLowerCase();
    return items.filter(
      (m) =>
        m.name.toLowerCase().includes(lo) ||
        m.providers.some((p) =>
          (p.apiServiceName || '').toLowerCase().includes(lo),
        ),
    );
  }, [items, q]);

  return (
    <div className="workspace-shell" style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 32px 80px' }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate('ai')}>
          <XIcons.IconArrowLeft size={12} /> AI 中转
        </button>
        <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>/</span>
        <span className="micro">Models</span>
      </div>
      <h1 style={{ margin: '8px 0 0', fontSize: 32, letterSpacing: '-0.015em', fontWeight: 500 }}>Models</h1>
      <p style={{ color: 'var(--fg-3)', fontSize: 14, marginTop: 8, marginBottom: 28 }}>
        Every model the xapi gateway supports — aggregated across all upstream providers.{' '}
        <span className="mono" style={{ color: 'var(--fg-4)' }}>{items.length} models</span>
      </p>

      <div style={{ position: 'relative', marginBottom: 24 }}>
        <XIcons.IconSearch
          size={14}
          style={{ position: 'absolute', left: 14, top: 14, color: 'var(--fg-4)', pointerEvents: 'none' }}
        />
        <input
          className="input"
          type="text"
          placeholder="Search models or providers…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ width: '100%', paddingLeft: 38, height: 42 }}
        />
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--fg-4)' }}>Loading…</div>
      ) : err ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--err)' }}>{err}</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--fg-4)' }}>
          {q ? `No models match "${q}".` : 'No models registered yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((m) => (
            <ModelRow key={m.slug} m={m} onClick={() => navigate('model-detail', { slug: m.slug })} />
          ))}
        </div>
      )}
      <div style={{ marginTop: 80 }}>
        <Footer />
      </div>
    </div>
  );
};

const ModelRow = ({ m, onClick }) => {
  const inMin = safeMin(m.providers.map((p) => per1M(p.pricing.inputPricePerToken)));
  const outMin = safeMin(m.providers.map((p) => per1M(p.pricing.outputPricePerToken)));
  const maxCtx = Math.max(...m.providers.map((p) => p.estimatedMaxTokens || 0));
  const streaming = m.providers.some((p) => p.supportsStreaming);
  // Pick the first provider that has a non-empty description — gives the row some context.
  const desc = m.providers.find((p) => p.apiServiceDescription)?.apiServiceDescription || '';
  // Avatar uses the first provider — for multi-provider models it acts as the
  // "primary" visual anchor. categoryMeta() gives the letter-fallback its tint
  // (without `meta` the avatar comes out flat grey and reads as broken).
  const primary = m.providers[0];

  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        padding: '16px 20px',
        border: '1px solid var(--ui-2)',
        borderRadius: 10,
        transition: 'border-color 0.15s, background 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--fg-3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--ui-2)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Anchor on the left. flexShrink: 0 sits inside ApiAvatar's baseStyle,
            so even when the description is long the avatar keeps its 36×36 box. */}
        <ApiAvatar
          name={primary?.apiServiceName || m.name}
          logoUrl={primary?.apiServiceLogo}
          meta={avatarMetaFor(primary)}
          size={36}
          radius={8}
          fontSize={14}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3
              className="mono"
              style={{ margin: 0, fontSize: 15, fontWeight: 500, color: 'var(--fg-1)' }}
            >
              {normalizeId(m.id || m.name)}
              {modelCategory(m.modality) !== 'chat' && <small style={{ marginLeft: 8, fontSize: 10 }}>{modelCategory(m.modality)?.toUpperCase()}</small>}
            </h3>
            {m.canonical ? (
              <span
                title="Canonical model"
                style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 3,
                  background: 'rgba(95,208,168,0.10)', border: '1px solid rgba(95,208,168,0.30)',
                  color: 'var(--c-mint)', letterSpacing: '0.04em', textTransform: 'uppercase',
                }}
              >canonical</span>
            ) : (
              <span
                title="Unmapped upstream model — admin should assign a canonical id"
                style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 3,
                  background: 'rgba(180,140,0,0.10)', border: '1px solid rgba(180,140,0,0.30)',
                  color: 'rgb(180,140,0)', letterSpacing: '0.04em', textTransform: 'uppercase',
                }}
              >unmapped</span>
            )}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--fg-3)',
              marginTop: 4,
              lineHeight: 1.55,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {m.vendor || m.family ? (
              <span>
                {vendorDisplay(m.vendor)}{m.family ? ` · ${m.family}` : ''}{' · '}
              </span>
            ) : null}
            via {m.providers.length} provider{m.providers.length > 1 ? 's' : ''}
            {desc ? <span style={{ color: 'var(--fg-4)' }}>{' · '}{desc}</span> : null}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div className="mono" style={{ fontSize: 13, color: 'var(--fg-2)' }}>
            {m.audioRates ? audioPriceText(m.audioRates, 'input') : <>{fmtUsd(inMin)} in · {fmtUsd(outMin)} out</>}
          </div>
          <div className="micro-mono" style={{ color: 'var(--fg-4)', marginTop: 3, fontSize: 10 }}>
            {m.audioRates ? 'Audio reference price' : 'per 1M tokens (min)'}
          </div>
        </div>
      </div>
      <div
        className="micro-mono"
        style={{
          display: 'flex',
          gap: 12,
          color: 'var(--fg-4)',
          marginTop: 10,
          fontSize: 11,
          paddingLeft: 50, // align with text column (36 avatar + 14 gap)
        }}
      >
        {streaming && <span>● stream</span>}
        {maxCtx > 0 && <span>max output {fmtCtx(maxCtx)}</span>}
        <span>{m.providers.length} provider{m.providers.length > 1 ? 's' : ''}</span>
      </div>
    </div>
  );
};

// ============ Detail page ============

export const ModelDetailPage = () => {
  const { route, navigate } = XShell.useRoute();
  const slug = route.params?.slug;
  const [model, setModel] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    XApi.modelsService
      .getBySlug(slug)
      .then((m) => {
        if (!cancelled) setModel(m);
      })
      .catch((e) => !cancelled && setErr(e.message || 'Model not found'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="workspace-shell" style={{ maxWidth: 1080, margin: '0 auto', padding: '60px 32px' }}>
        <div style={{ color: 'var(--fg-4)', textAlign: 'center', padding: 60 }}>Loading…</div>
      </div>
    );
  }
  if (err || !model) {
    return (
      <div className="workspace-shell" style={{ maxWidth: 1080, margin: '0 auto', padding: '60px 32px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'var(--err)', marginBottom: 16 }}>{err || `Model "${slug}" not found.`}</div>
          <button className="btn" onClick={() => navigate('models')}>
            <XIcons.IconArrowLeft size={12} /> Back to models
          </button>
        </div>
      </div>
    );
  }

  const inPrices = model.providers.map((p) => per1M(p.pricing.inputPricePerToken));
  const outPrices = model.providers.map((p) => per1M(p.pricing.outputPricePerToken));
  const inMin = safeMin(inPrices);
  const outMin = safeMin(outPrices);
  const maxCtx = Math.max(...model.providers.map((p) => p.estimatedMaxTokens || 0));
  const anyStreaming = model.providers.some((p) => p.supportsStreaming);
  const cacheCount = model.providers.filter((p) => p.pricing.cacheReadPricePerToken != null).length;

  return (
    <div className="workspace-shell" style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 32px 80px' }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate('ai')}>
          AI 中转
        </button>
        <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>/</span>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate('models')}>
          Models
        </button>
        <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>/</span>
        <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 12 }}>{model.slug}</span>
      </div>
      <div className="row gap-2" style={{ alignItems: 'center', margin: '12px 0 0' }}>
        <h1
          className="mono"
          style={{ margin: 0, fontSize: 30, letterSpacing: '-0.01em', fontWeight: 500 }}
        >
          {normalizeId(model.id || model.name)}
        </h1>
        {model.canonical ? (
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 3,
            background: 'rgba(95,208,168,0.10)', border: '1px solid rgba(95,208,168,0.30)',
            color: 'var(--c-mint)', letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>canonical</span>
        ) : (
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 3,
            background: 'rgba(180,140,0,0.10)', border: '1px solid rgba(180,140,0,0.30)',
            color: 'rgb(180,140,0)', letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>unmapped</span>
        )}
      </div>
      <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 8 }}>
        {model.vendor ? <span>{vendorDisplay(model.vendor)}{model.family ? ` · ${model.family}` : ''} · </span> : null}
        Available from {model.providers.length} provider{model.providers.length > 1 ? 's' : ''} on the xapi gateway.
      </div>

      {/* Stat strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
          marginTop: 24,
          marginBottom: 32,
        }}
      >
        <StatCard label="INPUT PRICE" value={model.audioRates ? audioPriceText(model.audioRates, 'input') : fmtUsd(inMin)} sub={model.audioRates ? "See provider audio rates" : "per 1M tokens (min)"} />
        <StatCard label="OUTPUT PRICE" value={model.audioRates ? audioPriceText(model.audioRates, 'output') : fmtUsd(outMin)} sub={model.audioRates ? "See provider audio rates" : "per 1M tokens (min)"} />
        <StatCard label="PROVIDERS" value={model.providers.length} sub="offering this model" />
        <StatCard label="MAX OUTPUT" value={fmtCtx(maxCtx)} sub={maxCtx > 0 ? 'tokens (estimated)' : '—'} />
        <StatCard label="STREAMING" value={anyStreaming ? 'Yes' : 'No'} sub={anyStreaming ? 'via SSE' : 'not supported'} />
        {cacheCount > 0 && (
          <StatCard label="CACHE PRICING" value={cacheCount} sub={`provider${cacheCount > 1 ? 's' : ''} expose cache rates`} />
        )}
      </div>

      <h2
        className="gs-section-title"
        style={{ fontSize: 18, fontWeight: 500, margin: '0 0 16px', letterSpacing: '-0.005em' }}
      >
        Providers for {normalizeId(model.id || model.name)}
      </h2>
      <p style={{ fontSize: 12.5, color: 'var(--fg-3)', margin: '0 0 18px', lineHeight: 1.6 }}>
        Each provider below is an upstream service routed through the xapi gateway. Per-provider pricing is the
        cost <em>after</em> any markup configured by the API owner.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {model.providers.map((p, idx) => (
          <ProviderCard key={`${p.apiServiceId}-${p.endpointId}-${idx}`} p={p} navigate={navigate} />
        ))}
      </div>
      <div style={{ marginTop: 80 }}>
        <Footer />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div style={{ padding: '14px 16px', border: '1px solid var(--ui-2)', borderRadius: 10 }}>
    <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div className="mono" style={{ fontSize: 22, fontWeight: 500, marginTop: 6, color: 'var(--fg-1)' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 2 }}>{sub}</div>}
  </div>
);

const ProviderCard = ({ p, navigate }) => {
  const inP = per1M(p.pricing.inputPricePerToken);
  const outP = per1M(p.pricing.outputPricePerToken);
  const cacheR = p.pricing.cacheReadPricePerToken != null ? per1M(p.pricing.cacheReadPricePerToken) : null;
  const cacheW =
    p.pricing.cacheCreationPricePerToken != null ? per1M(p.pricing.cacheCreationPricePerToken) : null;
  const tiers = Array.isArray(p.pricing.tiers) ? p.pricing.tiers : [];

  // host is the gateway-routable id; api-detail page resolves both id and host.
  const detailKey = p.apiServiceHost || p.apiServiceId;

  return (
    <div style={{ padding: 20, border: '1px solid var(--ui-2)', borderRadius: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        {/* Pass meta so the letter-fallback picks up category tint (avatars
            were rendering grey-on-grey before — looked broken). */}
        <ApiAvatar
          name={p.apiServiceName}
          logoUrl={p.apiServiceLogo}
          meta={avatarMetaFor(p)}
          size={36}
          radius={8}
          fontSize={14}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <a
            onClick={() => navigate('api-detail', { id: detailKey })}
            style={{ fontSize: 14, fontWeight: 500, cursor: 'pointer', color: 'var(--fg-1)', textDecoration: 'underline' }}
          >
            {p.apiServiceName}
          </a>
          <div className="mono" style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 2 }}>
            {p.endpointMethod} {p.endpointPath}
            {p.upstreamModelName ? <span> · upstream: <span style={{ color: 'var(--fg-3)' }}>{p.upstreamModelName}</span></span> : null}
          </div>
        </div>
        {p.supportsStreaming && (
          <span
            className="tag"
            style={{ fontSize: 11, padding: '3px 8px', border: '1px solid var(--ui-3)', borderRadius: 4 }}
          >
            stream
          </span>
        )}
      </div>

      {p.estimatedMaxTokens > 0 && (
        <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
          <div>
            <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.05em' }}>
              Max Output
            </div>
            <div className="mono" style={{ fontSize: 14, marginTop: 4 }}>{fmtCtx(p.estimatedMaxTokens)}</div>
          </div>
        </div>
      )}

      <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.05em', marginBottom: 8, fontWeight: 600 }}>
        {p.audioRates ? "AUDIO PRICING (reference)" : "PRICING"}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 16,
        }}
      >
        {p.audioRates && <p>Input: {audioPriceText(p.audioRates, 'input')} · OpenRouter: {audioPriceText(p.audioRates, 'input', true)}<br />Output: {audioPriceText(p.audioRates, 'output')} · OpenRouter: {audioPriceText(p.audioRates, 'output', true)}</p>}
        {!p.audioRates && <PriceCell label="Input Price" value={inP} />}
        {!p.audioRates && <PriceCell label="Output Price" value={outP} />}
        {cacheR != null && <PriceCell label="Cache Read" value={cacheR} />}
        {cacheW != null && <PriceCell label="Cache Write" value={cacheW} />}
      </div>

      {p.referencePrices && <ModelReferencePrices quotes={{currency: 'USD', unit: 'per_1m_tokens', settlement: 'upstream_cost', markupRatio: p.referencePrices.markupRatio, models:[{modelName: p.upstreamModelName, inputPer1M: inP, outputPer1M: outP, referenceInputPer1M: p.referencePrices.inputPer1M, referenceOutputPer1M: p.referencePrices.outputPer1M, referenceUpdatedAt: p.referencePrices.updatedAt}]}} />}
      {p.pricingMode === 'actual_cost' && <p style={{ color: 'var(--fg-4)', fontSize: 12 }}>Estimated from upstream rates and configured markup; charged by actual usage.</p>}
      {p.pricingMode === 'unknown' && <p style={{ color: 'var(--fg-4)', fontSize: 12 }}>Dynamic billing. See the provider for its billing rules.</p>}

      {tiers.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.05em', marginBottom: 8, fontWeight: 600 }}>
            VOLUME TIERS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {tiers.map((tier, i) => (
              <div
                key={i}
                className="mono"
                style={{ fontSize: 11.5, color: 'var(--fg-3)', display: 'flex', gap: 18, padding: '6px 10px', background: 'var(--ui-1)', borderRadius: 6 }}
              >
                <span>
                  {tier.inputTokenThreshold
                    ? `> ${fmtCtx(tier.inputTokenThreshold)} input tokens`
                    : `tier ${i + 1}`}
                </span>
                <span>in {fmtUsd(per1M(tier.inputPricePerToken))}</span>
                <span>out {fmtUsd(per1M(tier.outputPricePerToken))}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PriceCell = ({ label, value }) => (
  <div>
    <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.05em' }}>{label}</div>
    <div className="mono" style={{ fontSize: 15, marginTop: 4, color: 'var(--fg-1)' }}>{fmtUsd(value)}</div>
    <div style={{ fontSize: 10, color: 'var(--fg-4)', marginTop: 1 }}>/M tokens</div>
  </div>
);
